import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import axios, { AUTH_API, TOKEN_KEY, authHeaders } from "../lib/api";
import useLive from "../hooks/useLive";
import { Logo } from "../components/site/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AdminLogin } from "../components/admin/AdminLogin";
import { PlantForm } from "../components/admin/PlantForm";
import { StockForm } from "../components/admin/StockForm";
import { OrdersTable } from "../components/admin/OrdersTable";
import { AiSettings } from "../components/admin/AiSettings";
import { SupplyTable } from "../components/admin/SupplyTable";
import { RatesContact } from "../components/admin/RatesContact";
import { ProductionLog } from "../components/admin/ProductionLog";
import { HolidaysManager } from "../components/admin/HolidaysManager";
import { ReportsAdmin } from "../components/admin/ReportsAdmin";

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [checking, setChecking] = useState(!location.state?.user);
  const live = useLive(30000);

  useEffect(() => {
    if (user) return;
    if (!localStorage.getItem(TOKEN_KEY)) { setChecking(false); return; }
    axios.get(`${AUTH_API}/auth/me`, { headers: authHeaders() })
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    axios.post(`${AUTH_API}/auth/logout`, {}, { headers: authHeaders() }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) return <div className="min-h-screen" />;
  if (!user) return <AdminLogin onLogin={setUser} initialError={location.state?.error ?? ""} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b hairline">
        <div className="mx-auto max-w-6xl px-5 h-[72px] flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-[var(--sage)]">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 hover:bg-white/5"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Plant console</h1>
        <p className="mt-2 text-[var(--sage)]">Changes go live on the public site within 10 seconds.</p>
        <Tabs defaultValue="plant" className="mt-8">
          <TabsList className="bg-[var(--forest-2)] border hairline h-auto flex-wrap justify-start">
            <TabsTrigger data-testid="admin-tab-plant" value="plant">Production</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-stock" value="stock">Stock & price</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders">Orders</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-supply" value="supply">Farmer supply</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-rates" value="rates">Rates & contact</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-reports" value="reports">Test reports</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-ai" value="ai">AI assistant</TabsTrigger>
          </TabsList>
          <TabsContent value="plant" className="mt-6">{live.plant && <PlantForm key={live.plant.updated_at} plant={live.plant} onSaved={live.reload} />}<HolidaysManager onSaved={live.reload} /><ProductionLog onSaved={live.reload} /></TabsContent>
          <TabsContent value="stock" className="mt-6"><StockForm products={live.products} onSaved={live.reload} /></TabsContent>
          <TabsContent value="orders" className="mt-6"><OrdersTable products={live.products} onAuthFail={logout} /></TabsContent>
          <TabsContent value="supply" className="mt-6"><SupplyTable onAuthFail={logout} /></TabsContent>
          <TabsContent value="rates" className="mt-6"><RatesContact rates={live.rates} onSaved={live.reload} /></TabsContent>
          <TabsContent value="reports" className="mt-6"><ReportsAdmin products={live.products} onAuthFail={logout} /></TabsContent>
          <TabsContent value="ai" className="mt-6"><AiSettings /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
